import { AuthApiError } from "@okta/okta-auth-js";
import { OtpTransaction } from "./otpFlow";
import { OTP_STATE_HANDLE_STORAGE_KEY } from "./otpStorageKeys";

const EXPIRED_KEYS = new Set(["idx.session.expired", "expired", "session"]);

export type UnknownRecord = Record<string, unknown>;

const asRecord = (value: unknown): UnknownRecord | undefined =>
  typeof value === "object" && value !== null
    ? (value as UnknownRecord)
    : undefined;

const asArray = (value: unknown): unknown[] | undefined =>
  Array.isArray(value) ? value : undefined;

const extractErrorKeyFromString = (error?: string): string | null => {
  if (!error) return null;
  try {
    const parsed = JSON.parse(error);
    return parsed?.i18n?.key ?? null;
  } catch {
    return null;
  }
};

export const getStringProp = (
  value: unknown,
  propName: string
): string | undefined => {
  const record = asRecord(value);
  const propValue = record?.[propName];
  return typeof propValue === "string" ? propValue : undefined;
};

export const findByName = (
  items: unknown[] | undefined,
  name: string
): UnknownRecord | undefined => {
  if (!items) return undefined;
  return asRecord(items.find((item) => getStringProp(item, "name") === name));
};

export const getEnrollmentAuthenticator = (
  transaction: OtpTransaction
): UnknownRecord | undefined =>
  asRecord(
    transaction.context?.currentAuthenticatorEnrollment?.value ??
      transaction.rawIdxState?.currentAuthenticatorEnrollment?.value
  );

export const hasExpiredErrorKey = (errorMessage?: string): boolean => {
  const key = (extractErrorKeyFromString(errorMessage) ?? errorMessage)?.trim();
  if (!key) return false;
  const normalized = key.toLowerCase();

  return EXPIRED_KEYS.has(normalized);
};

export const extractAuthApiErrorI18nKey = (
  error: AuthApiError
): string | null => {
  const responseText = error.xhr?.responseText;
  if (!responseText) return null;

  try {
    const parsed = JSON.parse(responseText) as {
      messages?: {
        value?: Array<{
          i18n?: { key?: string };
        }>;
      };
    };

    return parsed.messages?.value?.[0]?.i18n?.key ?? null;
  } catch {
    return null;
  }
};

export const getOtpStateHandle = (transaction?: OtpTransaction | null) =>
  transaction?.context?.stateHandle ??
  transaction?.rawIdxState?.stateHandle ??
  sessionStorage.getItem(OTP_STATE_HANDLE_STORAGE_KEY) ??
  undefined;

export const isUserIdNotFoundError = (errorMessage: string): boolean => {
  return errorMessage.toLowerCase().includes("id not found");
};

export const getRemediationValues = (transaction: OtpTransaction): unknown[] =>
  transaction.rawIdxState?.remediation?.value ?? [];

export const getTransactionStateHandle = (
  transaction: OtpTransaction
): string => {
  const stateHandle =
    getStringProp(transaction.context, "stateHandle") ??
    getStringProp(transaction.rawIdxState, "stateHandle") ??
    getStringProp(transaction, "stateHandle");
  if (!stateHandle) {
    throw new Error("No stateHandle returned");
  }
  return stateHandle;
};

export const getItemFieldFromCollectionByProp = (
  source: unknown,
  collectionKey: string,
  matchProp: string,
  matchValue: string,
  returnProp: string
): string | undefined => {
  const collection = asRecord(source);
  const items = asArray(asRecord(collection?.[collectionKey])?.value);

  const matchedItem = asRecord(
    items?.find((item) => getStringProp(item, matchProp) === matchValue)
  );

  return getStringProp(matchedItem, returnProp);
};
